import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Search, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Import, 
  Loader2, 
  RefreshCw,
  Building,
  Users,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Filter,
  MoreHorizontal,
  FileText,
  BadgeCheck,
  ExternalLink,
  Ban,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useAuthContext } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingScreen } from '@/components/common';
import { Progress } from '@/components/ui/progress';

// Fixed interface for customer to accept any string for status
interface Customer {
  id: string;
  name: string;
  business_identification_number?: string;
  legal_name?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  status: string; // Accept any string for status
  domain?: string;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
  hubspot_owner_id?: string;
  responsible_user_name?: string;
  email?: string;
  industry?: string;
}

// Interface para os resultados da busca no HubSpot
interface HubSpotCompany {
  id: string;
  properties: {
    name: string;
    domain?: string;
    industry?: string;
    [key: string]: any;
  };
}

// Interface para os resultados da busca no Brasil API
interface BrasilApiResponse {
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  cep: string;
}

const Customers = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Estados para o modal de importação do HubSpot
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [hubspotSearchTerm, setHubspotSearchTerm] = useState('');
  const [hubspotSearchResults, setHubspotSearchResults] = useState<HubSpotCompany[]>([]);
  const [isSearchingHubspot, setIsSearchingHubspot] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<HubSpotCompany | null>(null);
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  const [isValidatingCnpj, setIsValidatingCnpj] = useState(false);
  const [importStep, setImportStep] = useState<'search' | 'cnpj'>('search');
  
  // Estados para o modal de sincronização
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [selectedCustomerForSync, setSelectedCustomerForSync] = useState<Customer | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Estado para controlar imagens com erro de carregamento
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '' && statusFilter === 'all') {
      setFilteredCustomers(customers);
    } else {
      let filtered = [...customers];
      
      // Apply text search filter
      if (searchTerm.trim()) {
        filtered = filtered.filter(customer => 
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.business_identification_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.domain?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply status filter
      if (statusFilter !== 'all') {
        filtered = filtered.filter(customer => customer.status === statusFilter);
      }
      
      setFilteredCustomers(filtered);
    }
    setCurrentPage(1);
  }, [customers, searchTerm, statusFilter]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredCustomers.length / pageSize));
  }, [filteredCustomers, pageSize]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setCustomers(data || []);
      setFilteredCustomers(data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      toast.error('Não foi possível carregar os clientes.');
    } finally {
      setLoading(false);
    }
  };

  // Format document helper (CNPJ or other identification)
  const formatDocument = (doc: string): string => {
    if (!doc) return '';
    
    const numbers = doc.replace(/[^\d]/g, '');
    
    if (numbers.length === 14) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    
    if (numbers.length === 11) {
      return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    
    return doc;
  };

  // Function to handle image loading errors
  const handleImageError = (customerId: string) => {
    setFailedImages(prev => ({ ...prev, [customerId]: true }));
  };

  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name.split(' ').map(part => part.charAt(0)).join('').toUpperCase().substring(0, 2);
  };

  const handleRowClick = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const previousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCustomers.slice(startIndex, endIndex);
  };

  const openImportDialog = () => {
    setIsImportDialogOpen(true);
    setHubspotSearchTerm('');
    setHubspotSearchResults([]);
    setSelectedCompany(null);
    setCnpjInput('');
    setCnpjError('');
    setImportStep('search');
  };

  const closeImportDialog = () => setIsImportDialogOpen(false);

  const formatCNPJ = (value: string): string => {
    const cnpj = value.replace(/[^\d]/g, '');
    if (cnpj.length <= 2) return cnpj;
    if (cnpj.length <= 5) return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
    if (cnpj.length <= 8) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
    if (cnpj.length <= 12) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
  };

  const handleCnpjInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCnpjInput(formatCNPJ(e.target.value));
    setCnpjError('');
  };

  const validateCNPJ = (cnpj: string): boolean => {
    cnpj = cnpj.replace(/[^\d]/g, '');
    if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
    let sum = 0, weight = 2;
    for (let i = 11; i >= 0; i--) {
      sum += parseInt(cnpj.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    let digit = 11 - (sum % 11);
    const dv1 = digit >= 10 ? 0 : digit;
    if (parseInt(cnpj.charAt(12)) !== dv1) return false;
    sum = 0; weight = 2;
    for (let i = 12; i >= 0; i--) {
      sum += parseInt(cnpj.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    digit = 11 - (sum % 11);
    const dv2 = digit >= 10 ? 0 : digit;
    return parseInt(cnpj.charAt(13)) === dv2;
  };

  const backToSearch = () => {
    setImportStep('search');
    setCnpjInput('');
    setCnpjError('');
  };

  const searchHubspotCompanies = async () => {
    if (!hubspotSearchTerm.trim()) return;
    setIsSearchingHubspot(true);
    setHubspotSearchResults([]);
    try {
      const { data, error } = await supabase.from('hubspotcompanies').select('id, name, domain, industry').ilike('name', `%${hubspotSearchTerm}%`).limit(10);
      if (error) throw error;
      if (data) {
        setHubspotSearchResults(data.map(c => ({ id: c.id, properties: { name: c.name, domain: c.domain, industry: c.industry } })));
      } else {
        setHubspotSearchResults([]);
      }
    } catch (error) {
      toast.error('Erro ao buscar empresas no HubSpot.');
    } finally {
      setIsSearchingHubspot(false);
    }
  };

  const selectCompany = (company: HubSpotCompany) => {
    setSelectedCompany(company);
    setImportStep('cnpj');
  };

  const validateAndImportCompany = async () => {
    if (!cnpjInput.trim()) { setCnpjError('CNPJ é obrigatório'); return; }
    const cnpj = cnpjInput.replace(/[^\d]/g, '');
    if (!validateCNPJ(cnpj)) { setCnpjError('CNPJ inválido'); return; }
    if (!selectedCompany) { toast.error('Nenhuma empresa selecionada'); return; }
    setIsValidatingCnpj(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) throw new Error('Could not get user session.');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-company-by-cnpj`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify({ cnpj })
      });
      if (!response.ok) throw new Error(`Erro ao buscar CNPJ: ${response.statusText}`);
      const data = await response.json();
      if (!data || !data.razao_social) throw new Error('Dados incompletos retornados pela API.');
      const formatTitleCase = (text: string) => text ? text.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';
      const enderecoCompleto = `${formatTitleCase(data.descricao_tipo_de_logradouro || '')} ${formatTitleCase(data.logradouro || '')}, ${data.numero || ''}${data.complemento ? ` - ${formatTitleCase(data.complemento)}` : ''}`;
      const { data: customerData, error: customerError } = await supabase.from('customers').insert({
        name: selectedCompany.properties.name,
        legal_name: data.razao_social,
        business_identification_number: cnpj,
        domain: selectedCompany.properties.domain,
        industry: selectedCompany.properties.industry || null,
        address: enderecoCompleto,
        address2: formatTitleCase(data.bairro || ''),
        city: formatTitleCase(data.municipio || ''),
        state: data.uf ? data.uf.toUpperCase() : '',
        zip: data.cep ? data.cep.replace(/[^\d]/g, '') : '',
        status: 'active'
      }).select().single();
      if (customerError) throw customerError;
      const { error: externalIdError } = await supabase.from('external_ids').insert({
        system_name: 'hubspot', external_id: selectedCompany.id, entity_id: customerData.id, entity_name: 'customers'
      });
      if (externalIdError) {
        await supabase.from('customers').delete().eq('id', customerData.id);
        throw externalIdError;
      }
      toast.success('Cliente importado com sucesso!');
      fetchCustomers();
      closeImportDialog();
    } catch (error) {
      toast.error(`Erro ao importar cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsValidatingCnpj(false);
    }
  };

  const openSyncDialog = (customer: Customer) => {
    setSelectedCustomerForSync(customer);
    setIsSyncDialogOpen(true);
  };

  const handleSyncCustomer = async () => {
    if (!selectedCustomerForSync) { toast.error('Nenhum cliente selecionado'); return; }
    setIsSyncing(true);
    try {
      const cnpj = selectedCustomerForSync.business_identification_number?.replace(/[^\d]/g, '') || '';
      if (!cnpj || !validateCNPJ(cnpj)) { toast.error('CNPJ inválido ou não encontrado'); setIsSyncing(false); return; }
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) throw new Error('Could not get user session.');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-company-by-cnpj`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify({ cnpj })
      });
      if (!response.ok) throw new Error(`Erro ao buscar CNPJ: ${response.statusText}`);
      const data = await response.json();
      if (!data || !data.razao_social) throw new Error('Dados incompletos retornados pela API.');
      const formatTitleCase = (text: string) => text ? text.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';
      const enderecoCompleto = `${formatTitleCase(data.descricao_tipo_de_logradouro || '')} ${formatTitleCase(data.logradouro || '')}, ${data.numero || ''}${data.complemento ? ` - ${formatTitleCase(data.complemento)}` : ''}`;
      
      const { data: externalIds, error: externalIdError } = await supabase.from('external_ids').select('*').eq('entity_id', selectedCustomerForSync.id).eq('system_name', 'hubspot').limit(1);
      let hubspotData = null;
      if (!externalIdError && externalIds && externalIds.length > 0) {
        const { data: hubspotCompany, error: hubspotError } = await supabase.from('hubspotcompanies').select('id, name, domain, industry').eq('id', externalIds[0].external_id).single();
        if (!hubspotError && hubspotCompany) hubspotData = hubspotCompany;
      }
      
      const updateData: Partial<Customer> = {
          legal_name: data.razao_social, address: enderecoCompleto, address2: formatTitleCase(data.bairro || ''), city: formatTitleCase(data.municipio || ''), state: data.uf ? data.uf.toUpperCase() : '', zip: data.cep ? data.cep.replace(/[^\d]/g, '') : '', updated_at: new Date().toISOString()
      };
      if (hubspotData) { updateData.domain = hubspotData.domain || updateData.domain; updateData.industry = hubspotData.industry || updateData.industry; }
      
      const { error } = await supabase.from('customers').update(updateData).eq('id', selectedCustomerForSync.id);
      if (error) throw error;
      
      toast.success('Cliente sincronizado com sucesso!');
      setIsSyncDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(`Erro ao sincronizar cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleInactivateCustomer = async (customer: Customer) => {
    try {
      const { error } = await supabase.from('customers').update({ status: 'inactive' }).eq('id', customer.id);
      if (error) throw error;
      toast.success('Cliente inativado com sucesso!');
      fetchCustomers();
    } catch (error) {
      toast.error(`Erro ao inativar cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const handleActivateCustomer = async (customer: Customer) => {
    try {
      const { error } = await supabase.from('customers').update({ status: 'active' }).eq('id', customer.id);
      if (error) throw error;
      toast.success('Cliente ativado com sucesso!');
      fetchCustomers();
    } catch (error) {
      toast.error(`Erro ao ativar cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const getStats = () => ({
    total: customers.length,
    active: customers.filter(c => c.status?.toLowerCase() === 'active').length,
    inactive: customers.filter(c => c.status?.toLowerCase() === 'inactive').length,
    pending: customers.filter(c => ['pending', 'suspended'].includes(c.status?.toLowerCase())).length
  });
  
  const stats = getStats();

  if (loading) return <LoadingScreen message="Carregando clientes..." size="x-small" />;

  return (
    <>
      <Helmet><title>CSM | Trivium - Clientes</title></Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Building className="h-6 w-6 text-white" /></div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Clientes</h1>
                    <p className="text-blue-100 text-base pb-2">Gerencie os clientes cadastrados no sistema</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={openImportDialog} className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200">
                  <Import className="h-4 w-4 mr-2" /> Importar do HubSpot
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4"><div className="flex items-start justify-between mb-3"><div className="flex items-center gap-2"><div className="p-1.5 bg-blue-100 rounded-md"><Users className="h-4 w-4 text-blue-600" /></div><span className="text-sm font-extrabold text-gray-600">Total</span></div><div className="text-2xl font-extrabold text-gray-900">{stats.total}</div></div><p className="text-xs text-gray-500">Clientes cadastrados</p></CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4"><div className="flex items-start justify-between mb-3"><div className="flex items-center gap-2"><div className="p-1.5 bg-green-100 rounded-md"><BadgeCheck className="h-4 w-4 text-green-600" /></div><span className="text-sm font-extrabold text-gray-600">Ativos</span></div><div className="text-2xl font-extrabold text-gray-900">{stats.active}</div></div><Progress value={stats.total > 0 ? (stats.active / stats.total) * 100 : 0} className="h-1.5" /></CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-4"><div className="flex items-start justify-between mb-3"><div className="flex items-center gap-2"><div className="p-1.5 bg-red-100 rounded-md"><Ban className="h-4 w-4 text-red-600" /></div><span className="text-sm font-extrabold text-gray-600">Inativos</span></div><div className="text-2xl font-extrabold text-gray-900">{stats.inactive}</div></div><Progress value={stats.total > 0 ? (stats.inactive / stats.total) * 100 : 0} className="h-1.5" /></CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4"><div className="flex items-start justify-between mb-3"><div className="flex items-center gap-2"><div className="p-1.5 bg-yellow-100 rounded-md"><Clock className="h-4 w-4 text-yellow-600" /></div><span className="text-sm font-extrabold text-gray-600">Pendentes</span></div><div className="text-2xl font-extrabold text-gray-900">{stats.pending}</div></div><p className="text-xs text-gray-500">Aguardando revisão</p></CardContent>
              </Card>
            </div>

            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Buscar clientes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 w-full h-10 bg-white" />
                  </div>
                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[180px] h-10 bg-white"><Filter className="h-4 w-4 mr-2 text-muted-foreground" /><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="suspended">Suspenso</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={fetchCustomers} className="h-10"><RefreshCw className="h-4 w-4 mr-2" /> Atualizar</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCustomers.length === 0 ? (
                  <div className="text-center py-20"><Users className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" /><h3 className="text-xl font-extrabold mb-2">Nenhum cliente encontrado</h3><p className="text-muted-foreground mb-6">Tente ajustar os filtros.</p></div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[80px] text-right"></TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>CNPJ</TableHead>
                          <TableHead>Cidade</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getCurrentPageItems().map((customer) => (
                            <TableRow key={customer.id} className="group hover:bg-muted/40 cursor-pointer" onClick={() => handleRowClick(customer.id)}>
                              <TableCell className="text-right">
                                <Avatar className="h-10 w-10 border ml-auto">
                                  {customer.domain && !failedImages[customer.id] ? (<AvatarImage src={`https://cdn.brandfetch.io/${customer.domain}?c=1idk4AUs2-buvooOUvQ`} alt={customer.name} onError={() => handleImageError(customer.id)} />)
                                  : customer.logo_url && !failedImages[customer.id] ? (<AvatarImage src={customer.logo_url} alt={customer.name} onError={() => handleImageError(customer.id)} />)
                                  : (<AvatarFallback className="bg-primary/10 text-primary">{getInitials(customer.name)}</AvatarFallback>)}
                                </Avatar>
                              </TableCell>
                              <TableCell className="font-extrabold"><div>{customer.name}{customer.domain && (<div className="text-xs text-muted-foreground">{customer.domain}</div>)}</div></TableCell>
                              <TableCell>{customer.business_identification_number ? formatDocument(customer.business_identification_number) : '-'}</TableCell>
                              <TableCell>{customer.city || '-'}</TableCell>
                              <TableCell>{customer.state || '-'}</TableCell>
                              <TableCell><StatusBadge status={customer.status} context="customer" /></TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/customers/${customer.id}`); }}><ArrowUpRight className="h-4 w-4 mr-2" />Ver detalhes</DropdownMenuItem>
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openSyncDialog(customer); }}><RefreshCw className="h-4 w-4 mr-2" />Sincronizar</DropdownMenuItem>
                                      {customer.status === 'inactive' ? (
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleActivateCustomer(customer); }}><BadgeCheck className="h-4 w-4 mr-2" />Ativar</DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleInactivateCustomer(customer); }}><Ban className="h-4 w-4 mr-2" />Inativar</DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center space-x-2">
                        <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                          <SelectTrigger className="h-8 w-[70px]"><SelectValue placeholder={pageSize.toString()} /></SelectTrigger>
                          <SelectContent side="top">
                            <SelectItem value="10">10</SelectItem><SelectItem value="20">20</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">Exibindo {Math.min((currentPage - 1) * pageSize + 1, filteredCustomers.length)} a {Math.min(currentPage * pageSize, filteredCustomers.length)} de {filteredCustomers.length} resultados</p>
                      </div>
                      <div className="flex items-center space-x-2">
                          <Button variant="outline" size="icon" onClick={previousPage} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                        <div className="text-sm">Página {currentPage} de {totalPages}</div>
                          <Button variant="outline" size="icon" onClick={nextPage} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            <div className="h-16" />
          </div>
        </div>
      </div>
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader><DialogTitle>{importStep === 'search' ? 'Importar do HubSpot' : 'Informar CNPJ'}</DialogTitle><DialogDescription>{importStep === 'search' ? 'Busque e selecione um cliente.' : 'Informe o CNPJ para completar a importação.'}</DialogDescription></DialogHeader>
          {importStep === 'search' ? (
            <><div className="flex items-center gap-2 mb-4"><Input placeholder="Digite o nome da empresa..." value={hubspotSearchTerm} onChange={(e) => setHubspotSearchTerm(e.target.value)} onKeyDown={(e) => {if (e.key === 'Enter' && hubspotSearchTerm.trim() && !isSearchingHubspot) {e.preventDefault();searchHubspotCompanies();}}} className="flex-1" /><Button onClick={searchHubspotCompanies} disabled={isSearchingHubspot || !hubspotSearchTerm.trim()}>{isSearchingHubspot ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}Buscar</Button></div>
              {hubspotSearchResults.length > 0 ? (<div className="max-h-[300px] overflow-y-auto">{hubspotSearchResults.map((company) => (<Card key={company.id} className="mb-2 cursor-pointer hover:bg-gray-50" onClick={() => selectCompany(company)}><CardContent className="p-4"><div className="font-extrabold">{company.properties.name}</div>{company.properties.domain && (<div className="text-sm text-gray-500">{company.properties.domain}</div>)}</CardContent></Card>))}</div>)
              : (isSearchingHubspot ? (<div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" /><p>Buscando empresas...</p></div>) : hubspotSearchTerm.trim() ? (<div className="text-center py-8 text-gray-500">Nenhuma empresa encontrada.</div>) : null)}</>
          ) : (
            <><div className="mb-4"><div className="bg-gray-50 p-4 rounded-md mb-4"><div className="font-extrabold">Empresa selecionada:</div><div>{selectedCompany?.properties.name}</div>{selectedCompany?.properties.domain && (<div className="text-sm text-gray-500">{selectedCompany.properties.domain}</div>)}</div><div className="space-y-4"><div className="space-y-2"><Label htmlFor="cnpj">CNPJ</Label><Input id="cnpj" placeholder="00.000.000/0000-00" value={cnpjInput} onChange={handleCnpjInputChange} onKeyDown={(e) => {if (e.key === 'Enter' && cnpjInput.trim() && !isValidatingCnpj) {e.preventDefault();validateAndImportCompany();}}} className={cnpjError ? "border-red-500" : ""} />{cnpjError && <p className="text-sm text-red-500">{cnpjError}</p>}</div></div></div></>
          )}
          <DialogFooter>{importStep === 'search' ? (<Button variant="outline" onClick={closeImportDialog}>Cancelar</Button>) : (<><Button variant="outline" onClick={backToSearch} className="mr-2">Voltar</Button><Button onClick={validateAndImportCompany} disabled={isValidatingCnpj || !cnpjInput.trim()}>{isValidatingCnpj ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Importar Cliente</Button></>)}</DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader><DialogTitle>Sincronizar Cliente</DialogTitle><DialogDescription>Confirme para sincronizar os dados do cliente com o CRM</DialogDescription></DialogHeader>
          <div className="mb-4"><div className="font-extrabold">Cliente selecionado:</div><div>{selectedCustomerForSync?.name}</div>{selectedCustomerForSync?.business_identification_number && (<div className="text-sm text-gray-500">{formatDocument(selectedCustomerForSync.business_identification_number)}</div>)}</div>
          <DialogFooter><Button variant="outline" onClick={() => setIsSyncDialogOpen(false)}>Cancelar</Button><Button onClick={handleSyncCustomer} disabled={isSyncing || !selectedCustomerForSync}>{isSyncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Sincronizar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Customers; 